import { Suspense, useCallback, useEffect, useRef, useState } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { MagicWand } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { CustomSelect } from "@/components/ui/custom-select.tsx"
import { FormError, FormField } from "@/components/ui/form.tsx"
import { TextArea } from "@/components/ui/input"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useClickAway } from "@/hooks/useClickAway"
import { CustomSofiaEvent, sofiaEventDispatch } from "@/hooks/useSofiaEventListener"
import { toast } from "@/hooks/useToast.ts"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { cn } from "@/lib/utils"
import { CareProviderValue } from "@/modules/care-provider/simple-search/care-provider-value"
import { showAIErrorMessages } from "@/modules/chats/lib/show-ai-error-messages"
import { DiagnosisCode } from "@/modules/patients/components/diagnosis-code.tsx"
import { DoctorInfo } from "@/modules/patients/components/doctor-info"
import { AiContent } from "@/modules/patients/detail/consultation-report/schema/consultation-report-schema"
import { summarizeConsultationReportQuery } from "@/modules/patients/detail/consultation-report/services/summarizeConsultationReportQuery"
import { DOCUMENT_LANGUAGES } from "@/modules/patients/hooks/useDefaultDocumentLanguage.ts"
import { DoctorType } from "@/modules/patients/information/doctors-network/doctors-network"
import { Gender } from "@/types/__generated__/union-types"

type ConsultationReportFormProps = {
  fetchKey?: number
  doctors: DoctorType
  appointmentId: string | null
  progressNoteId: string
  patientAge: number
  patientGender: Gender
  isEditMode: boolean
  onAiSuccess: (aiContent: AiContent) => void
}

const ConsultationReportForm = ({
  fetchKey,
  doctors,
  appointmentId,
  progressNoteId,
  patientAge,
  patientGender,
  isEditMode,
  onAiSuccess,
}: ConsultationReportFormProps) => {
  const { t } = useTranslation()
  const form = useFormContext()
  const {
    control,
    setValue,
    formState: { errors },
  } = form

  const healthcareProviderId = useWatch({ control, name: "healthcareProviderId" })
  const documentLanguage = useWatch({ control, name: "language" })
  const isAiGenerated = useWatch({ control, name: "isAiGenerated" })
  const hasSelection = !!healthcareProviderId

  const [listVisible, setListVisible] = useState(false)
  const clickAwayRef = useClickAway(() => setListVisible(false))

  const [isAiRunning, setIsAiRunning] = useState(false)
  const hasAutoTriggeredAiRef = useRef(false)

  const handleSelect = (id: string) => {
    setValue("healthcareProviderId", id, { shouldDirty: true, shouldValidate: true })
    setListVisible(false)
  }

  const handleRemove = () => {
    setValue("healthcareProviderId", "", { shouldDirty: true })
    setListVisible(false)
  }

  const handleMagicWand = useCallback(async () => {
    if (!appointmentId) return

    try {
      setIsAiRunning(true)
      sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", true))

      const aiFeatureId = uuidv4()
      const result = await summarizeConsultationReportQuery({
        age: patientAge,
        aiFeatureId,
        appointmentId,
        gender: patientGender,
        progressNoteId,
        targetAgentLanguage: documentLanguage,
      })

      if (!result) return

      if (result.summarizeConsultationReport.result === "SUCCESS") {
        const aiContent = {
          anamnesis: result.summarizeConsultationReport.anamnesisSummary,
          recommendedTreatment: result.summarizeConsultationReport.recommendedTreatment,
        }

        setValue("anamnesis", aiContent.anamnesis, { shouldDirty: true, shouldValidate: true })
        setValue("recommendedTreatment", aiContent.recommendedTreatment, { shouldDirty: true, shouldValidate: true })
        setValue("isAiGenerated", true, { shouldDirty: true })
        setValue("isReviewed", false, { shouldDirty: true })

        onAiSuccess(aiContent)

        FaroInsightsWrapper.pushEvent("aiFeature_consultation_report_summarizer", {
          aiFeatureId,
          feature: "Summarize consultation report",
          fileHash: result.summarizeConsultationReport.fileHash,
          aiRequestDurationMs: result.summarizeConsultationReport.aiRequestDurationMs.toString(),
          aiAnonymizationRequestDurationMs:
            result.summarizeConsultationReport.aiAnonymizationRequestDurationMs.toString(),
          sentAt: new Date().toISOString(),
        })
      } else {
        showAIErrorMessages(result.summarizeConsultationReport.result)
      }
    } catch {
      toast({
        description: t("chat.ai.errors.UNKNOWN_ERROR"),
      })
    } finally {
      setIsAiRunning(false)
      sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", false))
    }
  }, [appointmentId, patientAge, patientGender, progressNoteId, documentLanguage, setValue, onAiSuccess, t])

  useEffect(() => {
    if (hasAutoTriggeredAiRef.current) return
    if (isEditMode || !appointmentId) return

    hasAutoTriggeredAiRef.current = true
    handleMagicWand()
  }, [appointmentId, isEditMode, handleMagicWand])

  return (
    <div className="flex flex-col gap-4">
      <div className="pb-5">
        <FormField
          control={control}
          name="language"
          render={({ field: { value } }: { field: { value: string } }) => {
            return (
              <CustomSelect
                testId="document-language-select"
                onSelect={(val: string) => setValue("language", val, { shouldDirty: true })}
                value={value}
                label={`${t("patient.referralLetter.documentLanguage")}`}
                options={DOCUMENT_LANGUAGES}
                showValueLabel
                useTranslatedValue
                className="w-full"
              />
            )
          }}
        />
      </div>

      <div className="pb-5">
        <label className="text-on-surface text-base font-medium">{t("common.to")}</label>
        <FormField
          control={control}
          name="healthcareProviderId"
          render={() => (
            <div className="relative mt-2 flex flex-col gap-1">
              {hasSelection && (
                <Suspense fallback={<p className="h-12 rounded-xl border" />}>
                  <CareProviderValue value={healthcareProviderId} doctorNetworks={doctors} onRemove={handleRemove} />
                </Suspense>
              )}

              {!hasSelection && (
                <>
                  <input
                    placeholder={`${t("patient.consultationReport.careProvider")}*`}
                    className={cn(
                      "border-border bg-surface-subdued text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-hovered focus:outline-primary active:bg-surface-pressed h-12 w-full cursor-pointer rounded-md border pt-0 pl-4 text-sm outline-offset-0 transition-all focus:outline-2",
                      errors.healthcareProviderId && "outline-critical! outline-1 outline-offset-0 focus:outline-2",
                    )}
                    data-testid="care-provider-input"
                    readOnly
                    onClick={() => setListVisible(!listVisible)}
                  />
                  {listVisible && (
                    <div
                      ref={clickAwayRef}
                      className="bg-surface shadow-3 absolute top-full right-0 z-20 mt-1 w-full rounded-xl px-2 py-2">
                      {doctors.map((doctor) => (
                        <button
                          key={doctor.careProvider.id}
                          type="button"
                          className="hover:bg-surface-hovered w-full cursor-pointer rounded-xl px-2 py-1.5 text-left **:data-[testid=doctor-info]:bg-transparent"
                          onClick={() => handleSelect(doctor.careProvider.id)}>
                          <DoctorInfo
                            canHover={false}
                            role={doctor.role}
                            value={doctor.careProvider}
                            id={doctor.id}
                            readOnly
                            dense
                            careProviderId={doctor.careProvider?.id}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {errors.healthcareProviderId && <FormError>{t("common.fieldErrorList.requiredField")}</FormError>}
            </div>
          )}
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <p>{t("patient.consultationReport.content")}</p>
          <Button
            type="button"
            variant="circle"
            className="text-on-plum-surface size-auto p-2"
            isLoading={isAiRunning}
            data-testid="consultation-report-magic-wand-button"
            onClick={handleMagicWand}>
            <MagicWand className="size-5" />
          </Button>
        </div>
        <Controller
          control={control}
          name="diagnosisCode"
          render={({ field: { name, onChange, value }, fieldState: { invalid } }) => (
            <Suspense fallback={null}>
              <DiagnosisCode
                defaultValue={value}
                name={name}
                errors={errors}
                invalid={invalid}
                value={value}
                fetchKey={fetchKey}
                onSelect={onChange}
              />
            </Suspense>
          )}
        />

        <Controller
          name="anamnesis"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextArea
              label={t("patient.consultationReport.anamnesis")}
              rows={4}
              textLimit={1000}
              error={error?.message}
              translateError
              required
              data-testid="consultation-report-anamnesis"
              {...field}
            />
          )}
        />

        <Controller
          name="recommendedTreatment"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextArea
              label={t("patient.consultationReport.recommendedTreatment")}
              rows={4}
              textLimit={1000}
              error={error?.message}
              translateError
              required
              data-testid="consultation-report-recommended-treatment"
              {...field}
            />
          )}
        />
      </div>

      {isAiGenerated && (
        <FormField
          control={control}
          name="isReviewed"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div className="space-y-1">
              <DetailCheckbox
                checked={value}
                error={!!error}
                testId="consultation-report-reviewed-summary-checkbox"
                onCheckedChange={(checked) => {
                  onChange(checked === true)
                }}>
                {t("patient.consultationReport.reviewedSummary")}
              </DetailCheckbox>
              {error?.message && <FormError>{t(error.message)}</FormError>}
            </div>
          )}
        />
      )}
    </div>
  )
}

export { ConsultationReportForm }
